// PublisherAdvance.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Table,
    Button,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    Alert,
    UncontrolledTooltip,
    Spinner,
} from "reactstrap";
import {
    FaDollarSign,
    FaPlus,
    FaEdit,
    FaTrash,
    FaEye,
    FaDownload,
    FaExclamationTriangle,
    FaHistory,
    FaCheck,
    FaTimes,
    FaFileExport,
} from "react-icons/fa";
import { format } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReactBsTable from "components/Tables/ReactBsTable";
import NotificationAlert from "react-notification-alert";
import { addAdvanceData, publisherAdvanceList } from "actions/publisherAdvance";
import { getPublisherList } from "actions/publisher";
import { getPublisherAdvanceDetails } from "actions/publisherAdvance";
import { updateAdvanceData } from "actions/publisherAdvance";
import SearchableDropdown from "components/DropDown/SearchableDropdown";

// =============== MODAL STYLES ===============
const modalStyles = {
    header: {
        borderBottom: "2px solid #f0f0f0",
        paddingBottom: "18px",
        paddingTop: "18px",
    },
    title: {
        fontSize: "18px",
        fontWeight: "700",
        color: "#2c3e50",
        letterSpacing: "0.3px",
    },
    body: {
        padding: "24px",
        maxHeight: "calc(100vh - 200px)",
        overflowY: "auto",
    },
};

// =============== DUMMY DATA ===============
const DUMMY_PUBLISHERS = [
    {
        id: 1,
        name: "MediaMax Advertising",
        legalName: "MediaMax Advertising FZ-LLC",
        country: "UAE",
        trn: "100123456789003",
        paymentTerms: "Net 30",
        currency: "USD",
        email: "finance@mediamax.ae",
    },
    {
        id: 2,
        name: "Digital Reach Network",
        legalName: "Digital Reach Network LLC",
        country: "UAE",
        trn: "100987654321001",
        paymentTerms: "Net 45",
        currency: "USD",
        email: "accounts@digitalreach.com",
    },
    {
        id: 3,
        name: "Global Ad Solutions",
        legalName: "Global Ad Solutions Pte Ltd",
        country: "Singapore",
        trn: "200912345M",
        paymentTerms: "Net 15",
        currency: "USD",
        email: "finance@globalads.sg",
    },
    {
        id: 4,
        name: "EuroMedia Partners",
        legalName: "EuroMedia Partners GmbH",
        country: "Germany",
        trn: "DE123456789",
        paymentTerms: "Net 60",
        currency: "USD",
        email: "billing@euromedia.de",
    },
    {
        id: 5,
        name: "Asian Ad Network",
        legalName: "Asian Ad Network Ltd",
        country: "India",
        trn: "22ABCDE1234F1Z5",
        paymentTerms: "Net 30",
        currency: "USD",
        email: "accounts@asianad.in",
    },
];

// =============== HELPER FUNCTIONS ===============
const getFirstDayOfMonth = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}-01`;
};

const formatMonthDisplay = (monthString) => {
    if (!monthString) return "";
    try {
        const date = new Date(monthString);
        return format(date, "MMMM yyyy");
    } catch (e) {
        return monthString;
    }
};

const getMonthNumber = (monthString) => {
    if (!monthString) return null;
    try {
        const date = new Date(monthString);
        return date.getMonth() + 1;
    } catch (e) {
        return null;
    }
};

// Generate all months of the current year
const generateAllMonths = () => {
    const currentYear = new Date().getFullYear();
    const months = [];
    const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    for (let i = 0; i < 12; i++) {
        const monthNumber = i + 1;
        const monthString = `${currentYear}-${String(monthNumber).padStart(2, '0')}`;
        months.push({
            value: monthString,
            label: `${monthNames[i]} ${currentYear}`,
            monthNumber: monthNumber
        });
    }
    return months;
};

// =============== COMPONENTS ===============
const PublisherAdvance = () => {
    const notificationAlertRef = useRef(null);

    // State
    const [advances, setAdvances] = useState([]);
    const [filteredAdvances, setFilteredAdvances] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState("");
    const [selectedMonthNumber, setSelectedMonthNumber] = useState("");
    const [filterText, setFilterText] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [selectedAdvance, setSelectedAdvance] = useState(null);
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    // Modal states
    const [isAdvanceModalOpen, setIsAdvanceModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [publisherOptions, setPublisherOptions] = useState([]);

    // Form state
    const [advanceForm, setAdvanceForm] = useState({
        publisherId: "",
        amount: "",
        month: new Date(),
        description: "",
    });

    const [editForm, setEditForm] = useState({
        id: null,
        publisherId: "",
        amount: "",
        month: new Date(),
        description: "",
    });

    // All months of current year
    const allMonths = generateAllMonths();

    // Stats
    const [stats, setStats] = useState({
        totalAdvances: 0,
        totalAmount: 0,
        totalUsed: 0,
        totalRemaining: 0,
        totalPublishers: 0,
    });

const monthPickerStyles = `
        .styled-month-input {
            border: 1px solid #1336a0 !important;
            border-radius: 12px !important;
            height: 44px !important;
            padding: 0.75rem 0.9rem !important;
            font-size: 0.95rem !important;
            background: linear-gradient(180deg, #ff0000 0%, #f8faff 100%) !important;
            box-shadow: 0 2px 8px rgba(185, 185, 185, 0.08) !important;
            transition: all 0.2s ease !important;
        }
        .styled-month-input:focus {
            border-color: #b7b7b7 !important;
            box-shadow: 0 0 0 4px rgba(108, 108, 109, 0.12) !important;
            outline: none !important;
        }
        .styled-month-picker {
            border: none !important;
            border-radius: 16px !important;
            box-shadow: 0 18px 45px rgba(15, 23, 42, 0.16) !important;
            overflow: hidden !important;
            background: #ffffff !important;
        }
        .styled-month-picker .react-datepicker__header {
            background: linear-gradient(135deg, #3b3a3a 0%, #8a8a8a 100%) !important;
            border-bottom: none !important;
            padding-top: 14px !important;
        }
        .styled-month-picker .react-datepicker__current-month,
        .styled-month-picker .react-datepicker-time__header,
        .styled-month-picker .react-datepicker-year-header {
            color: #ffffff !important;
            font-weight: 700 !important;
        }
        .styled-month-picker .react-datepicker__navigation {
            top: 7px !important;
               align-items: center !important;
              
        }
        .styled-month-picker .react-datepicker__navigation-icon::before {
            border-color: #ffffff !important;
               align-items: center !important;
              
        }
        .styled-month-picker .react-datepicker__month-text {
            width: 88px !important;
            padding: 12px 0 !important;
            border-radius: 10px !important;
            margin: 5px 0 !important;
            font-size: 0.9rem !important;
            color: #243b53 !important;
        }
        .styled-month-picker .react-datepicker__month-text:hover {
            background: #cdcdcd !important;
            color: #121212 !important;
        }
        .styled-month-picker .react-datepicker__month-text--selected,
        .styled-month-picker .react-datepicker__month-text--keyboard-selected {
            background: linear-gradient(135deg, #a2a2a2 0%, #1a1818 100%) !important;
            color: #ffffff !important;
            box-shadow: 0 8px 18px rgba(143, 143, 143, 0.22) !important;
        }
        .styled-month-picker-popper {
            z-index: 1300 !important;
        }
    `;

    // Get user data from localStorage
    const userData = JSON.parse(localStorage.getItem("p"));
    const currentUser = userData?.role || "Admin";

    // =============== NOTIFICATIONS ===============
    const notify = (type, message) => {
        if (notificationAlertRef.current) {
            notificationAlertRef.current.notificationAlert({
                place: "tr",
                message: <div>{message}</div>,
                type,
                icon: "tim-icons icon-bell-55",
                autoDismiss: 5,
            });
        }
    };

    // =============== EFFECTS ===============
    useEffect(() => {
        // Default: show all months (no month filter)
        setSelectedMonth("");
        setSelectedMonthNumber("");

        // Fetch without month param to get all records by default
        fetchAdvances();
        getPublisherOptions();
    }, []);

    useEffect(() => {
        filterAdvances();
        calculateStats();
    }, [advances, selectedMonth, selectedMonthNumber, filterText]);

    // =============== FETCH ADVANCES ===============
    const fetchAdvances = async (monthNumber) => {

        try {
            setIsLoading(true);
            // Call API with monthNumber only when provided; otherwise fetch all
            let response;
            if (typeof monthNumber !== "undefined" && monthNumber !== "" && monthNumber !== null) {
                response = await publisherAdvanceList(pageNum, monthNumber);
            } else {
                response = await publisherAdvanceList(pageNum);
            }


            if (response?.code === 200 && response?.data) {
                const data = response.data;
                const advanceData = data.PublisherAdvance || [];

                const mappedAdvances = advanceData.map((item) => ({
                    id: item.ID || item.id,
                    publisherId: item.publisherID || item.publisherId,
                    publisherName: item.companyName || `Publisher ${item.publisherID}`,
                    month: item.month ? format(new Date(item.month), "yyyy-MM") : "",
                    monthDisplay: formatMonthDisplay(item.month),
                    amount: item.amount || 0,
                    usedAmount: item.usedAmount || 0,
                    addedOn: item.createdAt,
                    remainingAmount: item.remainingAmount || 0,
                    currency: "USD",
                    date: item.month ? format(new Date(item.month), "yyyy-MM-dd") : "",
                    description: item.description || "",
                    status: item.status || (item.remainingAmount > 0 ? "active" : "used"),
                    createdBy: item.createdBy || "",
                    createdAt: item.createdAt || "",
                    paymentDate: item.paymentDate || "",
                }));

                setAdvances(mappedAdvances);
                setTotalRecords(data.totalRecords || mappedAdvances.length);

            } else {
                setAdvances([]);
                setTotalRecords(0);
                notify("danger", response?.message || "No advance data found");
            }
        } catch (error) {
            console.error("Error fetching advances:", error);
            setAdvances([]);
            setTotalRecords(0);
            notify("danger", "Failed to fetch advance data");
        } finally {
            setIsLoading(false);
        }
    };

    const filterAdvances = () => {
        let filtered = advances;

        // Filter by month number
        if (selectedMonthNumber) {
            filtered = filtered.filter(a => {
                const monthNum = getMonthNumber(a.month);
                return monthNum === parseInt(selectedMonthNumber);
            });
        }

        // Filter by search text
        if (filterText) {
            filtered = filtered.filter(a =>
                a.publisherName.toLowerCase().includes(filterText.toLowerCase())
            );
        }

        setFilteredAdvances(filtered);
    };

    const calculateStats = () => {
        let monthAdvances = advances;

        if (selectedMonthNumber) {
            monthAdvances = advances.filter(a => {
                const monthNum = getMonthNumber(a.month);
                return monthNum === parseInt(selectedMonthNumber);
            });
        }

        const totalAmount = monthAdvances.reduce((sum, a) => sum + a.amount, 0);
        const totalUsed = monthAdvances.reduce((sum, a) => sum + a.usedAmount, 0);
        const totalRemaining = monthAdvances.reduce((sum, a) => sum + a.remainingAmount, 0);
        const uniquePublishers = new Set(monthAdvances.map(a => a.publisherId));

        setStats({
            totalAdvances: monthAdvances.length,
            totalAmount: totalAmount,
            totalUsed: totalUsed,
            totalRemaining: totalRemaining,
            totalPublishers: uniquePublishers.size,
        });
    };

    // =============== API CALLS ===============
    const getPublisherOptions = async (searchString = "") => {
        try {
            const res = await getPublisherList(0, [], searchString);
            const options = res?.code === 200
                ? (res?.data?.Publisher || []).map((item) => ({
                    ID: item?.ID || item?.id || "",
                    label: item?.companyName || item?.name || "",
                    value: item?.ID || item?.id || "",
                }))
                : [];
            setPublisherOptions(options);
        } catch (error) {
            console.error("Failed to load publisher options", error);
            const fallbackOptions = DUMMY_PUBLISHERS.map(p => ({
                ID: p.id,
                label: p.name,
                value: p.id,
            }));
            setPublisherOptions(fallbackOptions);
        }
    };

    const handleAddAdvance = async () => {
        if (!advanceForm.publisherId || !advanceForm.amount || !advanceForm.description) {
            notify("danger", "Please fill all required fields");
            return;
        }

        if (parseFloat(advanceForm.amount) <= 0) {
            notify("danger", "Amount must be greater than 0");
            return;
        }

        try {
            setIsLoading(true);

            const selectedPublisher = publisherOptions.find(
                p => String(p.ID) === String(advanceForm.publisherId)
            );

            const payload = {
                publisherID: parseInt(advanceForm.publisherId),
                month: getFirstDayOfMonth(advanceForm.month),
                amount: parseFloat(advanceForm.amount),
                description: advanceForm.description,
                createdBy: currentUser,
            };
            const response = await addAdvanceData(payload);
            if (response?.code === 200) {
                notify("success", "Advance payment added successfully!");
                setIsAdvanceModalOpen(false)
                setAdvanceForm({
                    publisherId: "",
                    amount: "",
                    month: new Date(),
                    description: "",
                })
                // Refresh the list with current month
                await fetchAdvances(parseInt(selectedMonthNumber));
            } else {
                notify("danger", response?.message || "Failed to add advance payment");
            }
        } catch (error) {
            console.error("Error adding advance:", error);
            notify("danger", "Something went wrong while adding advance payment");
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditAdvance = async () => {
        if (!editForm.publisherId || !editForm.amount || !editForm.description) {
            notify("danger", "Please fill all required fields");
            return;
        }

        if (parseFloat(editForm.amount) <= 0) {
            notify("danger", "Amount must be greater than 0");
            return;
        }

        try {
            setIsLoading(true);
            const payload = {
                publisherID: editForm.publisherId,
                month: getFirstDayOfMonth(editForm.month),
                amount: parseFloat(editForm.amount),
                remainingAmount: parseFloat(editForm.amount),
                description: editForm.description,
                createdBy: currentUser,
            };

            const response = await updateAdvanceData(editForm.id, payload);

            if (response?.code === 200) {
                await fetchAdvances(parseInt(selectedMonthNumber));
                notify("success", "Advance payment added successfully!");
                setIsEditModalOpen(false);
                setAdvanceForm({
                    publisherId: "",
                    amount: "",
                    month: new Date(),
                    description: "",
                });
            } else {
                notify("danger", response?.message || "Failed to add advance payment");
            }
        } catch (error) {
            console.error("Error adding advance:", error);
            notify("danger", "Something went wrong while adding advance payment");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteAdvance = () => {
        setAdvances(prev => prev.filter(a => a.id !== selectedAdvance?.id));
        setIsDeleteModalOpen(false);
        setSelectedAdvance(null);
        notify("success", "Advance payment deleted successfully!");
    };

    const handleSizePerPageChange = (e) => {
        const newSize = Number(e.target.value);
        setPageSize(newSize);
        setPageNum(1);
    };

    const handlePageChange = (page) => {
        setPageNum(page);
        // Pass the current month number when changing page
        if (selectedMonthNumber) {
            fetchAdvances(parseInt(selectedMonthNumber));
        } else {
            fetchAdvances();
        }
    };

    const handleMonthFilterChange = (date) => {
        const monthValue = date
            ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
            : "";

        setSelectedMonth(monthValue);

        if (monthValue) {
            const monthData = allMonths.find(m => m.value === monthValue);
            const monthNumber = monthData?.monthNumber || 1;
            setSelectedMonthNumber(String(monthNumber));
            fetchAdvances(monthNumber);
        } else {
            setSelectedMonthNumber("");
            fetchAdvances();
        }
    };

    const resetFilters = () => {
        setSelectedMonth("");
        setSelectedMonthNumber("");
        setFilterText("");
        setPageNum(1);
        // Fetch all records
        fetchAdvances();
    };

    const openEditModal = async (advance) => {
        setSelectedAdvance(advance);
        const res = await getPublisherAdvanceDetails(advance.id)

        setEditForm({
            id: res.data.ID,
            publisherId: res.data.publisherID,
            amount: String(res.data.amount),
            month: new Date(res.data.date || res.data.month),
            description: res.data.description,
        });
        setIsEditModalOpen(true);
    };

    const openDeleteModal = (advance) => {
        setSelectedAdvance(advance);
        setIsDeleteModalOpen(true);
    };

    const openViewModal = (advance) => {
        setSelectedAdvance(advance);
        setIsViewModalOpen(true);
    };

    // =============== RENDER HELPERS ===============
    const getStatusBadge = (status) => {
        const statusMap = {
            active: { color: "success", label: "Active" },
            partial: { color: "warning", label: "Partial" },
            COMPLETED: { color: "success", label: "Completed" },
            PENDING: { color: "warning", label: "Pending" },
        };
        const s = statusMap[status] || statusMap.active;
        return <Badge color={s.color}>{s.label}</Badge>;
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount || 0);
    };

    // =============== TRUNCATE TEXT HELPER ===============
    const truncateText = (text, maxLength = 30) => {
        if (!text) return "";
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + "...";
    };



    // =============== TABLE COLUMNS ===============
    const columns = [
        {
            dataField: "publisherName",
            text: "Publisher",
            formatter: (cell, row) => (
                <div>
                    <strong>{row.publisherName}</strong>
                </div>
            ),
            sort: true,
        },
        {
            dataField: "description",
            text: "Description",
            formatter: (cell) => {
                if (!cell) return <span className="text-muted">No description</span>;
                const truncated = truncateText(cell, 30);

                if (truncated === cell) {
                    return <span>{cell}</span>;
                }

                const uniqueId = `desc-${Math.random().toString(36).substr(2, 9)}`;
                return (
                    <>
                        <span
                            id={uniqueId}
                            style={{ cursor: 'pointor', borderBottom: '1px dashed #ccc' }}
                        >
                            {truncated}
                        </span>
                        <UncontrolledTooltip delay={0} target={uniqueId}>
                            {cell}
                        </UncontrolledTooltip>
                    </>
                );
            },
            sort: true,
            headerAlign: "left",
            align: "left",
        },
        {
            dataField: "monthDisplay",
            text: "Month",
            sort: true,
        },
        {
            dataField: "amount",
            text: "Amount",
            formatter: (cell) => formatCurrency(cell),
            headerAlign: "right",
            align: "right",
            sort: true,
        },

        {
            dataField: "remainingAmount",
            text: "Remaining",
            formatter: (cell) => <span className="text-success fw-bold">{formatCurrency(cell)}</span>,
            headerAlign: "right",
            align: "right",
            sort: true,
        },
        {
            dataField: "status",
            text: "Status",
            formatter: (cell) => getStatusBadge(cell),
            sort: true,
        },
            {
            dataField: "addedOn",
            text: "Added On",
            sort: true,
        },
        {
            dataField: "actions",
            text: "Actions",
            formatter: (cell, row) => {
                const rowId = row.id;
                return (
                    <div style={{ display: "flex", gap: "4px", justifyContent: "center", flexWrap: "wrap" }}>
                        <a
                            className="table-action table-action-info"
                            id={`view-${rowId}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                openViewModal(row);
                            }}
                        >
                            <i className="fas fa-eye" />
                        </a>
                        <UncontrolledTooltip delay={0} target={`view-${rowId}`}>
                            View Details
                        </UncontrolledTooltip>

                        <a
                            className="table-action table-action-info"
                            id={`edit-${rowId}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                openEditModal(row);

                            }}
                        >
                            <i className="fas fa-edit" />
                        </a>
                        <UncontrolledTooltip delay={0} target={`edit-${rowId}`}>
                            Edit Advance
                        </UncontrolledTooltip>

                    
                    </div>
                );
            },
            headerAlign: "center",
            align: "center",
        }
    ];

    // =============== FILTER SECTION ===============
    const filterSection = () => (
        <Row className="align-items-center">
            <Col md={3}>
                <FormGroup>
                  
                    <DatePicker
                        selected={selectedMonth ? new Date(`${selectedMonth}-01T00:00:00`) : null}
                        onChange={handleMonthFilterChange}
                         className="form-control"
                        // className="form-control styled-month-input"
                        calendarClassName="styled-month-picker"
                        popperClassName="styled-month-picker-popper"
                        dateFormat="MMMM yyyy"
                        showMonthYearPicker
                        placeholderText="Select Month / Year"
                        // isClearable
                    />
                </FormGroup>
            </Col>
            <Col md={3}>
                <FormGroup>
                    <Input
                        placeholder="Search by publisher..."
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                    />
                </FormGroup>
            </Col>
            <Col md={4} className="d-flex align-items-center gap-2">

                <Button className="secondary" onClick={resetFilters}>
                    Reset
                </Button>
            </Col>



        </Row>
    );

    // =============== RENDER ===============
    return (
        <>
            <style>{monthPickerStyles}</style>
            <div className="rna-container">
                <NotificationAlert ref={notificationAlertRef} />
            </div>
            <Container className="mt--6" fluid>
                <Card>
                    <ReactBsTable
                        title="Publisher Advances"
                        description="Manage publisher advance payments and track usage"
                        data={filteredAdvances}
                        columns={columns}
                        keyField="id"
                        renderFilters={filterSection}
                        page={pageNum}
                        sizePerPage={pageSize}
                        totalRecords={totalRecords}
                        onPageChange={handlePageChange}
                        showLoader={isLoading}
                        handleSizePerPageChange={handleSizePerPageChange}
                        // enablePageSizeManagement={true}
                        addbtntext="Add Advance"
                        onAddBtnClick={() => setIsAdvanceModalOpen(true)}
                        bordered
                        hover
                    />
                </Card>

                {/* ========== MODALS ========== */}

                {/* Add Advance Modal */}
                <Modal isOpen={isAdvanceModalOpen} toggle={() => setIsAdvanceModalOpen(false)} centered size="lg">
                    <ModalHeader toggle={() => setIsAdvanceModalOpen(false)} style={modalStyles.header}>
                        <h5 style={modalStyles.title}>
                            Add Advance Payment
                        </h5>
                    </ModalHeader>
                    <ModalBody style={modalStyles.body}>
                        <Form>
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Select Publisher <span className="text-danger">*</span></Label>
                                        <Input
                                            type="select"
                                            value={advanceForm.publisherId}
                                            onChange={(e) => setAdvanceForm({ ...advanceForm, publisherId: e.target.value })}
                                        >
                                            <option value="">Select Publisher</option>
                                            {publisherOptions.map(p => (
                                                <option key={p.ID} value={p.ID}>{p.label}</option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Month <span className="text-danger">*</span></Label>
                                           <DatePicker
                        selected={advanceForm.month}
                                            onChange={(date) => setAdvanceForm({ ...advanceForm, month: date })}
                         className="form-control"
                        // className="form-control styled-month-input"
                        calendarClassName="styled-month-picker"
                        popperClassName="styled-month-picker-popper"
                        dateFormat="MMMM yyyy"
                        showMonthYearPicker
                        placeholderText="Select Month / Year"
                        // isClearable
                    />
                                        
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Amount (USD) <span className="text-danger">*</span></Label>
                                        <Input
                                            type="number"
                                            placeholder="Enter amount in USD"
                                            value={advanceForm.amount}
                                            onChange={(e) => setAdvanceForm({ ...advanceForm, amount: e.target.value })}
                                            min="0.01"
                                            step="0.01"
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <FormGroup>
                                <Label>Description <span className="text-danger">*</span></Label>
                                <Input
                                    type="textarea"
                                    placeholder="Enter description..."
                                    value={advanceForm.description}
                                    onChange={(e) => setAdvanceForm({ ...advanceForm, description: e.target.value })}
                                    rows={3}
                                />
                            </FormGroup>
                        </Form>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setIsAdvanceModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            className="filter-btn-gradient-primary btn-md responsive"
                            onClick={handleAddAdvance}
                            disabled={isLoading || !advanceForm.publisherId || !advanceForm.amount || !advanceForm.description}
                        >
                            {isLoading ? <><Spinner size="sm" className="me-1" /> Saving...</> : "Add Advance"}
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* Edit Advance Modal */}
                <Modal isOpen={isEditModalOpen} toggle={() => setIsEditModalOpen(false)} centered size="lg">
                    <ModalHeader toggle={() => setIsEditModalOpen(false)} style={modalStyles.header}>
                        <h5 style={modalStyles.title}>
                            Edit Advance Payment
                        </h5>
                    </ModalHeader>
                    <ModalBody style={modalStyles.body}>
                        <Form>
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Select Publisher <span className="text-danger">*</span></Label>
                                        <Input
                                            type="select"
                                            value={editForm.publisherId}
                                            onChange={(e) => setEditForm({ ...editForm, publisherId: e.target.value })}
                                        >
                                            <option value="">Select Publisher</option>
                                            {publisherOptions.map(p => (
                                                <option key={p.ID} value={p.ID}>{p.label}</option>
                                            ))}
                                        </Input>
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Month <span className="text-danger">*</span></Label>
                                        <DatePicker
                                            selected={editForm.month}
                                            onChange={(date) => setEditForm({ ...editForm, month: date })}
                                            className="form-control"
                                            dateFormat="MMMM yyyy"
                                            showMonthYearPicker
                                            placeholderText="Select Month"
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Amount (USD) <span className="text-danger">*</span></Label>
                                        <Input
                                            type="number"
                                            placeholder="Enter amount in USD"
                                            value={editForm.amount}
                                            onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                                            min="0.01"
                                            step="0.01"
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <FormGroup>
                                <Label>Description <span className="text-danger">*</span></Label>
                                <Input
                                    type="textarea"
                                    placeholder="Enter description..."
                                    value={editForm.description}
                                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                    rows={3}
                                />
                            </FormGroup>

                        </Form>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setIsEditModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            className="filter-btn-gradient-primary btn-md responsive"
                            onClick={handleEditAdvance}
                            disabled={!editForm.publisherId || !editForm.amount || !editForm.description}
                        >
                            Update Advance
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* View Advance Modal */}
                <Modal isOpen={isViewModalOpen} toggle={() => setIsViewModalOpen(false)} centered size="lg">
                    <ModalHeader toggle={() => setIsViewModalOpen(false)} style={modalStyles.header}>
                        <h5 style={modalStyles.title}>
                             Advance Details
                        </h5>
                    </ModalHeader>
                    <ModalBody style={modalStyles.body}>
                        {selectedAdvance && (
                            <>
                                <Row className="px-3">
                                    <Col md={6} className="mb-2">
                                        <FormGroup className="has-label d-flex flex-column align-items-start">
                                            <Label className="text-muted small text-uppercase">Publisher</Label>
                                            <span className="font-weight-bold">
                                                {selectedAdvance.publisherName}
                                            </span>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6} className="mb-2">
                                        <FormGroup className="has-label d-flex flex-column align-items-start">
                                            <Label className="text-muted small text-uppercase">Month</Label>
                                            <span className="font-weight-bold">
                                                {selectedAdvance.monthDisplay}
                                            </span>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row className="px-3">
                                    <Col md={6} className="mb-2">
                                        <FormGroup className="has-label d-flex flex-column align-items-start">
                                            <Label className="text-muted small text-uppercase">Amount</Label>
                                            <span className="font-weight-bold">
                                                {formatCurrency(selectedAdvance.amount)}
                                            </span>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6} className="mb-2">
                                        <FormGroup className="has-label d-flex flex-column align-items-start">
                                            <Label className="text-muted small text-uppercase">Used Amount</Label>
                                            <span className="font-weight-bold text-warning">
                                                {formatCurrency(selectedAdvance.usedAmount)}
                                            </span>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row className="px-3">
                                    <Col md={6} className="mb-2">
                                        <FormGroup className="has-label d-flex flex-column align-items-start">
                                            <Label className="text-muted small text-uppercase">Remaining Amount</Label>
                                            <span className="font-weight-bold text-success">
                                                {formatCurrency(selectedAdvance.remainingAmount)}
                                            </span>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6} className="mb-2">
                                        <FormGroup className="has-label d-flex flex-column align-items-start">
                                            <Label className="text-muted small text-uppercase">Status</Label>
                                            <span>
                                                {getStatusBadge(selectedAdvance.status)}
                                            </span>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row className="px-3">
                                    <Col md={6} className="mb-2">
                                        <FormGroup className="has-label d-flex flex-column align-items-start">
                                            <Label className="text-muted small text-uppercase">Date</Label>
                                            <span className="font-weight-bold">
                                                {selectedAdvance.date || selectedAdvance.month}
                                            </span>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6} className="mb-2">
                                        <FormGroup className="has-label d-flex flex-column align-items-start">
                                            <Label className="text-muted small text-uppercase">Created By</Label>
                                            <span className="font-weight-bold">
                                                {selectedAdvance.createdBy || "-"}
                                            </span>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row className="px-3">
                                    <Col md={12} className="mb-2">
                                        <FormGroup className="has-label d-flex flex-column align-items-start">
                                            <Label className="text-muted small text-uppercase">Description</Label>
                                            <span className="font-weight-bold">
                                                {selectedAdvance.description || "-"}
                                            </span>
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setIsViewModalOpen(false)}>
                            Close
                        </Button>
                    </ModalFooter>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal isOpen={isDeleteModalOpen} toggle={() => setIsDeleteModalOpen(false)} centered>
                    <ModalHeader toggle={() => setIsDeleteModalOpen(false)} style={modalStyles.header}>
                        <h5 style={modalStyles.title}>
                            <FaTrash className="me-2 text-danger" /> Confirm Delete
                        </h5>
                    </ModalHeader>
                    <ModalBody>
                        <Alert color="danger">
                            <FaExclamationTriangle className="me-2" />
                            <strong>Warning:</strong> This action cannot be undone.
                        </Alert>
                        {selectedAdvance && (
                            <p>
                                Are you sure you want to delete the advance payment of{' '}
                                <strong>{formatCurrency(selectedAdvance.amount)}</strong> for{' '}
                                <strong>{selectedAdvance.publisherName}</strong> for{' '}
                                <strong>{selectedAdvance.monthDisplay}</strong>?
                            </p>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setIsDeleteModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button color="danger" onClick={handleDeleteAdvance}>
                            <FaTrash className="me-1" /> Delete
                        </Button>
                    </ModalFooter>
                </Modal>
            </Container>
        </>
    );
};

export default PublisherAdvance;